import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import { formatPrice } from '@spotahome/soyuz-currency';

import {
  FREQUENCY_MONTHLY,
  PAYMENT_TO_LANDLORD,
  APPLY_ON_INCLUDED,
  COST_CATEGORY_BILLS,
  COST_CATEGORY_MONTHLY_RENT
} from '../../constants/costs';

import { formatMonthlyPaymentDate } from '../utils/dateUtils';

import OffersLabel from '../OffersLabel';

import BillsLabelIcon from '../icons/BillsLabelIcon';

import { ListingCostItemShape } from '../../constants/shapes';

import { capitalizeFirstLetter } from '../utils/stringUtils';

import PaymentTimelineCard from './PaymentTimelineCard';
import PaymentTimeLineItem from './PaymentTimelineItem';
import PaymentTimelineSubtitle from './PaymentTimelineSubtitle';

import styles from './PaymentTimelineCard.module.scss';

const getCostSubtitle = cost => (
  <PaymentTimelineSubtitle
    from={cost.coveredPeriod?.from || null}
    to={cost.coveredPeriod?.to || null}
    paymentTo={cost.payTo || null}
    frequency={cost.frequency || null}
  />
);

const getTooltipText = (cost, index) => {
  if (index === 0 && cost.applyOn === APPLY_ON_INCLUDED) {
    return trans(
      'spotahomeBookingPaymentInfo.downPaymentFirstMonthStrategy.tooltip.fullMonth'
    );
  }

  if (index === 1 && cost.applyOn === APPLY_ON_INCLUDED) {
    return trans(
      'spotahomeBookingPaymentInfo.downPaymentFirstMonthStrategy.tooltip.partialMonth'
    );
  }

  return null;
};

const isSomeBillsIncluded = billsCosts =>
  !!billsCosts.length && billsCosts.some(c => c.applyOn === APPLY_ON_INCLUDED);

const isAllBillsIncluded = billsCosts =>
  !!billsCosts.length && billsCosts.every(c => c.applyOn === APPLY_ON_INCLUDED);

const getTags = ({ billsCosts, offers, appliedOfferValue }) => {
  const isSomeBills = isSomeBillsIncluded(billsCosts);
  const isAllBills = isAllBillsIncluded(billsCosts);
  const tags = [
    {
      tag: () => (
        <OffersLabel
          offers={offers}
          appliedOfferValue={
            appliedOfferValue > 0 ? `${appliedOfferValue}%` : null
          }
          clickable
          showIcon
          size="big"
        />
      ),
      applicable: offers?.length > 0 || appliedOfferValue > 0
    },
    {
      tag: {
        label: isAllBills
          ? trans('listing.labels.bills.all.title')
          : trans('listing.labels.bills.some.title'),
        renderLeadingIcon: () => <BillsLabelIcon size="24" />,
        isSpaced: true,
        size: 'big'
      },
      applicable: isSomeBills || isAllBills
    }
  ];

  return tags.filter(t => t.applicable !== false).map(t => t.tag);
};

const formatCostTitle = type =>
  type
    .split('|')
    .map(part => trans(capitalizeFirstLetter(part.trim())))
    .join(' · ');

const getBillValue = bill =>
  bill.payTo !== APPLY_ON_INCLUDED && bill.value === null
    ? trans('listing.bills.is-not-included')
    : bill.value;

const getBillValueSubtitle = (bill, currencyCode) => {
  if (bill.value === null && bill.applyOn !== APPLY_ON_INCLUDED) {
    return trans('listing.bills.estimation-requested-to-landlord');
  }

  if (bill.applyOn === APPLY_ON_INCLUDED && bill.value > 0) {
    return trans('listing.bills.amount-included-no-type', {
      billAmount: formatPrice(bill.value, currencyCode),
      currency: ''
    });
  }

  return null;
};

const SubsectionTitle = ({ title, frequency, payTo }) => (
  <div>
    <h5 className={styles['payment-timeline-card__section-title']}>{title}</h5>
    {(frequency || payTo) && (
      <div>
        <PaymentTimelineSubtitle frequency={frequency} paymentTo={payTo} />
      </div>
    )}
  </div>
);

const DuringStay = ({
  offers = [],
  appliedOfferValue = null,
  isPricingCalculated,
  listingMonthlyPrice = null,
  costs = {},
  currencyCode,
  isCollapsed = true
}) => {
  const duringStayCosts = costs?.costs || [];
  const totalValue = isPricingCalculated
    ? formatPrice(costs?.total, currencyCode)
    : listingMonthlyPrice;

  const totalValueWithoutOffer =
    isPricingCalculated && costs?.totalWithoutOffer
      ? formatPrice(costs?.totalWithoutOffer, currencyCode)
      : null;

  const monthlyRentCosts = duringStayCosts.filter(
    c => c.category === COST_CATEGORY_MONTHLY_RENT
  );

  const billsCosts = duringStayCosts.filter(
    c => c.category === COST_CATEGORY_BILLS
  );

  const otherCosts = duringStayCosts.filter(
    c =>
      c.category !== COST_CATEGORY_MONTHLY_RENT &&
      c.category !== COST_CATEGORY_BILLS
  );

  return (
    <PaymentTimelineCard
      title={trans('payment.timeline.section.during_stay.title')}
      value={trans('price.per.month', { price: totalValue })}
      valueWithoutOffer={
        totalValueWithoutOffer > 0
          ? trans('price.per.month', {
              price: totalValueWithoutOffer
            })
          : null
      }
      isCollapsed={isCollapsed}
      collapsible={isPricingCalculated}
      tags={getTags({ offers, billsCosts, appliedOfferValue })}
    >
      {monthlyRentCosts.length > 0 && (
        <>
          <SubsectionTitle
            title={trans('listing.pricing.monthly-rent')}
            frequency={FREQUENCY_MONTHLY}
            payTo={monthlyRentCosts[0]?.payTo || PAYMENT_TO_LANDLORD}
          />
          {monthlyRentCosts.map((cost, index) => (
            <PaymentTimeLineItem
              key={`${cost.type}-${cost.paymentDate}`}
              value={cost.value}
              valueWithoutOffer={cost.valueWithoutOffer}
              currencyCode={currencyCode}
              title={capitalizeFirstLetter(
                formatMonthlyPaymentDate(cost.paymentDate)
              )}
              subtitle={getCostSubtitle(cost)}
              included={cost.applyOn === APPLY_ON_INCLUDED && index === 0}
              includedCopy={trans(
                'spotahomeBookingPaymentInfo.downPaymentFirstMonthStrategy.firstMonthCovered'
              )}
              tooltipText={getTooltipText(cost, index)}
            />
          ))}
        </>
      )}

      {billsCosts.length > 0 && (
        <>
          <SubsectionTitle
            title={trans('listing.bills.title')}
            payTo={billsCosts[0]?.payTo || PAYMENT_TO_LANDLORD}
          />
          {billsCosts.map(cost => (
            <PaymentTimeLineItem
              key={`${cost.type}`}
              value={getBillValue(cost)}
              valueSubtitle={getBillValueSubtitle(cost, currencyCode)}
              currencyCode={currencyCode}
              title={formatCostTitle(cost.type)}
              subtitle={getCostSubtitle(cost)}
              included={cost.applyOn === APPLY_ON_INCLUDED}
            />
          ))}
        </>
      )}

      {otherCosts.length > 0 && (
        <>
          <SubsectionTitle title={trans('payment.timeline.other.costs')} />
          {otherCosts.map(cost => (
            <PaymentTimeLineItem
              key={`${cost.type}-${cost.paymentDate}`}
              title={
                cost.name ?? trans(`payment.timeline.cost.label.${cost.type}`)
              }
              value={cost.value}
              currencyCode={currencyCode}
              subtitle={getCostSubtitle(cost)}
              tooltipText={cost.description || null}
            />
          ))}
        </>
      )}
    </PaymentTimelineCard>
  );
};

DuringStay.propTypes = {
  offers: PropTypes.arrayOf(PropTypes.shape({})),
  appliedOfferValue: PropTypes.number,
  isPricingCalculated: PropTypes.bool.isRequired,
  listingMonthlyPrice: PropTypes.string,
  costs: PropTypes.shape(ListingCostItemShape),
  currencyCode: PropTypes.string.isRequired,
  isCollapsed: PropTypes.bool
};

export default DuringStay;
